import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { BADGE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

/**
 * Semantic tone for a `MetaChip`. `neutral` recedes to a muted grey built from the text palette;
 * `primary` is the brand tint (for a "this belongs to the primary entity" annotation, e.g. a role/
 * position badge); the four status tones map onto the theme status palette (never a hardcoded hex),
 * reading as one family with `CandidateStatusChip` / `EmployeeStatusChip`.
 */
export type MetaChipTone = "neutral" | "primary" | "info" | "success" | "warning" | "error";

/**
 * Fill / ink / border for a chip whose colour comes from a **domain scale** rather than from a
 * semantic tone — a coverage-deficit tier, an HPPD band, a PBJ status range. Those scales already
 * live as named theme tokens and are read by the surfaces they grade, so a chip that legends one has
 * to be able to draw itself from the SAME tokens; a `tone` would be a second, drifting source.
 *
 * Still tokens, never hex literals at the call site: pass values that came from `theme.palette` or
 * from a named token group. `border` is optional — omit it for a fill-only chip.
 */
export type MetaChipCustomTone = { ink: string; bg: string; border?: string };

type Props = {
	/** The short, already-translated label to display. */
	label?: string;
	/** Optional icon, sized to the chip's text. */
	icon?: ReactNode;
	/** Where `icon` sits. Use `end` for an icon that shows a state, like a bell, so it reads after the name. */
	iconPlacement?: "start" | "end";
	/** Marks a clickable chip as an on/off toggle for screen readers. Ignored without `onClick`. */
	ariaPressed?: boolean;
	/** Semantic tone — drives fill/ink/border from theme tokens. Default `neutral`. */
	tone?: MetaChipTone;
	/**
	 * Accent color for a chip whose meaning is a **category** rather than a status — a bucket color
	 * that has no place in the status palette (e.g. `PUNCH_SUMMARY_CATEGORY_DOT_HEX[category]`). Pass
	 * the categorical token, never a literal hex. Overrides `tone`, deriving the same fill/ink/border
	 * ratios from it so a categorical chip still reads as one family with the status ones.
	 */
	accentColor?: string;
	/**
	 * Opt-in override of the tone's fill/ink/border with a domain scale's own tokens. When present it
	 * wins over `tone`; when absent nothing changes, so every existing caller is untouched.
	 *
	 * Distinct from `accentColor`, which DERIVES a fill and an edge from one categorical colour. Pass
	 * this when the scale already names all three values (a coverage-deficit tier, an HPPD band) and the
	 * chip has to draw the exact tokens the surface it legends uses. Precedence, when more than one is
	 * somehow set: `customTone` → `accentColor` → `tone`, most specific first.
	 */
	customTone?: MetaChipCustomTone;
	/** Optional hover text; the label ellipsizes to its container width. */
	tooltip?: string;
	/** When provided the chip becomes an interactive button (press-scale, hover, keyboard). */
	onClick?: (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
	/** Accessible label for the button variant (defaults to `label`). */
	ariaLabel?: string;
	/**
	 * Renders a trailing "×" that removes the chip — for a chip that stands for a revocable thing
	 * (an attached file, an applied filter token) rather than a read-only annotation. Additive: a chip
	 * without it is unchanged. The × is a real `<button>` (so it is tabbable and operable from the
	 * keyboard) and stops its click from reaching `onClick`, so a removable chip can also be tappable.
	 */
	onRemove?: (event: MouseEvent<HTMLElement>) => void;
	/** Accessible label for the remove button — required in practice whenever `onRemove` is set. */
	removeLabel?: string;
	removeDataTestId?: string;
	size?: "small" | "medium";
	/** Layout overrides — the caller owns width/alignment, not the chip's ink. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A small labelled micro-chip with an optional leading icon and a semantic tone — the generic
 * annotation chip for dense rows / headers (role, date, facility, "needs attention"). Read-only by
 * default; pass `onClick` to make it an accessible button (press-scale, reduced-motion safe), and/or
 * `onRemove` to give it a trailing "×" for a revocable thing (an attached file, a filter token). All
 * color comes from theme tokens, so it never drifts to a hardcoded value. For selectable filter chips
 * use `SystemChips`; for domain interview/employment status use `CandidateStatusChip`/`EmployeeStatusChip`.
 */
export const MetaChip: FC<Props> = ({
	label,
	icon,
	// DEFAULT: leading, so existing callers keep their layout.
	iconPlacement = "start",
	ariaPressed,
	tone = "neutral",
	accentColor,
	customTone,
	tooltip,
	onClick,
	ariaLabel,
	onRemove,
	removeLabel,
	removeDataTestId,
	size = "small",
	sx,
	dataTestId,
}) => {
	const interactive = !!onClick;

	// When interactive, render a `role="button"` span (not a real <button>) with keyboard handling so
	// the chip can live inside another button (e.g. a clickable list-item card) without an invalid
	// nested-button / hydration error.
	const handleKeyDown = interactive
		? (event: KeyboardEvent<HTMLElement>) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onClick?.(event);
				}
			}
		: undefined;

	const chip = (
		<Box
			component="span"
			data-testid={dataTestId}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			role={interactive ? "button" : undefined}
			tabIndex={interactive ? 0 : undefined}
			aria-label={interactive ? (ariaLabel ?? label) : undefined}
			aria-pressed={interactive ? ariaPressed : undefined}
			sx={[
				(theme) => {
					// One categorical colour, with the fill and the edge derived from it at the same ratios the
					// semantic tones use — so a categorical chip reads as one family with the status ones.
					const accentTokens = accentColor ? { ink: accentColor, bg: alpha(accentColor, 0.12), border: alpha(accentColor, 0.26) } : undefined;
					const semanticTokens =
						tone === "neutral"
							? {
									ink: theme.palette.text.secondary,
									bg: alpha(theme.palette.text.secondary, 0.1),
									border: alpha(theme.palette.text.secondary, 0.18),
								}
							: {
									ink: theme.palette[tone].dark,
									bg: alpha(theme.palette[tone].main, 0.12),
									border: alpha(theme.palette[tone].main, 0.26),
								};
					// Most specific source wins: a domain scale's own three values, then a derived categorical
					// colour, then the semantic tone.
					// DEFAULT: `customTone.border` is optional and falls back to `transparent` rather than to a
					// computed edge — a 1px transparent border keeps the chip's box model identical to a toned
					// one, so a fill-only chip is exactly as tall as its toned siblings on the same line.
					const tokens = customTone ? { ink: customTone.ink, bg: customTone.bg, border: customTone.border ?? "transparent" } : (accentTokens ?? semanticTokens);
					return {
						display: "inline-flex",
						alignItems: "center",
						gap: 0.5,
						maxWidth: "100%",
						minHeight: size === "small" ? BADGE.STATUS_CHIP_HEIGHT_SM : BADGE.STATUS_CHIP_HEIGHT_MD,
						px: 0.75,
						py: 0.25,
						m: 0,
						borderRadius: 1.5,
						border: `1px solid ${tokens.border}`,
						backgroundColor: tokens.bg,
						color: tokens.ink,
						fontFamily: "inherit",
						fontSize: size === "small" ? theme.typography.caption.fontSize : theme.typography.body2.fontSize,
						fontWeight: 600,
						lineHeight: 1.2,
						cursor: interactive ? "pointer" : "default",
						textAlign: "start",
						whiteSpace: "nowrap",
						overflow: "hidden",
						"& > svg": { fontSize: size === "small" ? 14 : 16, flexShrink: 0 },
						...(interactive && {
							transition: theme.transitions.create(["background-color", "transform"], { duration: 140 }),
							"@media (hover: hover) and (pointer: fine)": {
								"&:hover": {
									// A domain scale has no `main` to deepen, so an interactive custom-tone chip keeps
									// its own fill on hover rather than borrowing an unrelated palette's. A
									// categorical accent DOES deepen, at the same ratio as a semantic tone.
									backgroundColor: customTone
										? customTone.bg
										: accentColor
											? alpha(accentColor, 0.2)
											: tone === "neutral"
												? alpha(theme.palette.text.secondary, 0.16)
												: alpha(theme.palette[tone].main, 0.2),
								},
							},
							"&:active": { transform: "scale(0.97)" },
							"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
						}),
					};
				},
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{iconPlacement === "start" && icon}
			{!!label && (
				<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
					{label}
				</Box>
			)}
			{iconPlacement === "end" && icon}
			{!!onRemove && (
				<Box
					component="button"
					type="button"
					aria-label={removeLabel}
					data-testid={removeDataTestId}
					onClick={(event: MouseEvent<HTMLElement>) => {
						// The chip itself may be clickable (`onClick`); removing is not "choosing" it.
						event.stopPropagation();
						onRemove(event);
					}}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
						p: 0,
						ml: 0.25,
						border: "none",
						background: "none",
						color: "inherit",
						cursor: "pointer",
						opacity: 0.7,
						"&:hover, &:focus-visible": { opacity: 1 },
						// The chip's own `& > svg` rule can't reach this glyph (it is a grandchild), so size it here.
						"& > svg": { fontSize: size === "small" ? 14 : 16 },
					}}
				>
					<CloseRoundedIcon />
				</Box>
			)}
		</Box>
	);

	if (!tooltip) {
		return chip;
	}

	return <InHouseTooltip title={tooltip}>{chip}</InHouseTooltip>;
};

export default MetaChip;
